import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Users, LogOut } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { householdsApi, formatError } from "../lib/api";

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, setHouseholdId, logout } = useAuth();
  const [mode, setMode] = useState("choose"); // choose | create | join
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const createHousehold = async (e) => {
    e?.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const h = await householdsApi.create(name.trim());
      setHouseholdId(h.id);
      toast.success(`Created "${h.name}" — your join code is ${h.join_code}`);
      navigate("/");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const joinHousehold = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const h = await householdsApi.join(code.trim().toUpperCase());
      setHouseholdId(h.id);
      toast.success(`Joined "${h.name}"`);
      navigate("/");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col px-6 py-10">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#277A82]">
          Hi, {user?.name?.split(" ")[0]}
        </div>
        <h1 className="font-heading text-3xl font-bold text-[#2D3142] mt-1">
          Set up your household
        </h1>
        <p className="text-[#4F5D75] mt-2 text-sm">
          A household is a shared fridge. Anyone with the code can see and update containers.
        </p>
      </motion.div>

      {mode === "choose" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mt-8 space-y-3"
        >
          <button
            onClick={() => setMode("create")}
            data-testid="onboarding-create-btn"
            className="w-full tup-card p-5 text-left hover:bg-[#F5F3ED] transition-colors flex items-start gap-4"
          >
            <div className="w-11 h-11 rounded-xl bg-[#DCEDF0] flex items-center justify-center shrink-0">
              <Plus className="w-5 h-5 text-[#1F5566]" />
            </div>
            <div>
              <div className="font-heading font-semibold text-[#2D3142] text-lg">Create a household</div>
              <div className="text-sm text-[#4F5D75] mt-1">
                Start fresh. You'll get a join code to invite roommates or family.
              </div>
            </div>
          </button>

          <button
            onClick={() => setMode("join")}
            data-testid="onboarding-join-btn"
            className="w-full tup-card p-5 text-left hover:bg-[#F5F3ED] transition-colors flex items-start gap-4"
          >
            <div className="w-11 h-11 rounded-xl bg-[#FBEAE5] flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-[#B04A2A]" />
            </div>
            <div>
              <div className="font-heading font-semibold text-[#2D3142] text-lg">Join a household</div>
              <div className="text-sm text-[#4F5D75] mt-1">
                Have a 6-character code from your roommate or partner? Enter it here.
              </div>
            </div>
          </button>

          <button
            onClick={() => { logout(); navigate("/auth"); }}
            data-testid="onboarding-logout-btn"
            className="w-full text-sm text-[#4F5D75] mt-4 py-2 flex items-center justify-center gap-2"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </motion.div>
      )}

      {mode === "create" && (
        <motion.form
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={createHousehold}
          data-testid="create-household-form"
          className="mt-8 space-y-4"
        >
          <div>
            <Label className="text-[#2D3142] font-semibold mb-2 block">Household name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Apartment 4B"
              data-testid="household-name-input"
              className="bg-white border-[#E2E0D8] h-12"
              autoFocus
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            data-testid="create-household-submit-btn"
            className="w-full bg-[#277A82] hover:bg-[#1F5566] text-white h-12"
          >
            {busy ? "Creating…" : "Create household"}
          </Button>
          <button type="button" onClick={() => setMode("choose")} className="w-full text-sm text-[#4F5D75]">
            ← Back
          </button>
        </motion.form>
      )}

      {mode === "join" && (
        <motion.form
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={joinHousehold}
          data-testid="join-household-form"
          className="mt-8 space-y-4"
        >
          <div>
            <Label className="text-[#2D3142] font-semibold mb-2 block">Join code</Label>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABC123"
              data-testid="join-code-input"
              className="bg-white border-[#E2E0D8] h-12 font-mono tracking-[0.4em] text-center text-lg"
              maxLength={8}
              autoFocus
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            data-testid="join-household-submit-btn"
            className="w-full bg-[#277A82] hover:bg-[#1F5566] text-white h-12"
          >
            {busy ? "Joining…" : "Join household"}
          </Button>
          <button type="button" onClick={() => setMode("choose")} className="w-full text-sm text-[#4F5D75]">
            ← Back
          </button>
        </motion.form>
      )}
    </div>
  );
};

export default Onboarding;
